import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import communityService, {
  GameLineupPlayerItem,
  GameTimelineEvent,
} from '@/services/communityService';

const ASSIST_NONE_VALUE = 'none';

interface CatatGolModalProps {
  isOpen: boolean;
  onClose: () => void;
  gameSlug: string;
  teamSide: 'home' | 'away';
  teamName: string;
  onFieldPlayers: GameLineupPlayerItem[];
  currentTimeline: GameTimelineEvent[];
  capturedTimeStr?: string;
  capturedSeconds?: number;
  onSuccess: () => void;
}

const CatatGolModal = ({
  isOpen,
  onClose,
  gameSlug,
  teamSide,
  teamName,
  onFieldPlayers,
  currentTimeline,
  capturedTimeStr = '00:00',
  capturedSeconds = 0,
  onSuccess,
}: CatatGolModalProps) => {
  const [scorerId, setScorerId] = useState<string>('');
  const [assistId, setAssistId] = useState<string>(ASSIST_NONE_VALUE);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setScorerId('');
      setAssistId(ASSIST_NONE_VALUE);
      setError(null);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const canSave = scorerId !== '';

  const handleSave = async () => {
    if (!canSave || saving) return;
    setError(null);
    setSaving(true);
    try {
      const calculatedMinute = Math.floor(capturedSeconds / 60);
      const timeTag = `${calculatedMinute}'`;

      const newEvent: GameTimelineEvent = {
        time: timeTag,
        type: 'goal',
        team_side: teamSide,
        player_id: parseInt(scorerId, 10),
        assist_id: assistId === ASSIST_NONE_VALUE || !assistId ? null : parseInt(assistId, 10),
        detail: null,
      };

      const nextTimeline = [...currentTimeline, newEvent];
      await communityService.updateGameTimeline(gameSlug, nextTimeline);
      onSuccess();
      onClose();
    } catch (err: unknown) {
      const msg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      setError(msg || (err instanceof Error ? err.message : 'Gagal menyimpan gol'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center sm:items-center p-0 sm:p-4 animate-in fade-in duration-200 !mt-0"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 space-y-5 shadow-2xl relative max-h-[90vh] overflow-y-auto animate-in slide-in-from-bottom duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle indicator */}
        <div className="w-12 h-1.5 bg-gray-200 rounded-full mx-auto" />

        {/* Header Title & Close Button */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl font-extrabold text-[#0E1726]">Catat Gol</h2>
            <p className="text-xs font-semibold text-gray-400 mt-1">
              {teamName} · menit {capturedTimeStr}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-500 transition-colors shrink-0 cursor-pointer"
          >
            <Icon icon="ph:x-bold" className="w-4 h-4" />
          </button>
        </div>

        {error && (
          <div className="text-xs text-red-600 bg-red-50 border border-red-200 px-3.5 py-2.5 rounded-2xl">
            {error}
          </div>
        )}

        <div className="space-y-5 max-h-[55vh] overflow-y-auto pr-1">
          {/* Section 1: Pencetak Gol */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
              Pencetak gol
            </label>
            <div className="space-y-2">
              {onFieldPlayers.map((p) => {
                const pIdStr = String(p.player_participant_id);
                const isSelected = scorerId === pIdStr;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setScorerId(pIdStr)}
                    className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'border-[#FC480D] bg-orange-50/50 shadow-xs'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon
                        icon="ph:soccer-ball-bold"
                        className={`w-5 h-5 ${isSelected ? 'text-[#FC480D]' : 'text-gray-400'}`}
                      />
                      <span
                        className={`text-sm font-semibold ${
                          isSelected ? 'text-[#0E1726]' : 'text-gray-700'
                        }`}
                      >
                        {p.name}
                      </span>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                        isSelected
                          ? 'border-[#FC480D] bg-[#FC480D] text-white'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <Icon icon="ph:check-bold" className="w-3 h-3" />}
                    </div>
                  </button>
                );
              })}
              {onFieldPlayers.length === 0 && (
                <p className="text-xs text-gray-400 italic py-2">Tidak ada pemain di lapangan</p>
              )}
            </div>
          </div>

          {/* Section 2: Assist */}
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wider">
              Assist
            </label>
            <div className="space-y-2">
              {onFieldPlayers.map((p) => {
                const pIdStr = String(p.player_participant_id);
                if (pIdStr === scorerId) return null;
                const isSelected = assistId === pIdStr;
                return (
                  <button
                    type="button"
                    key={p.id}
                    onClick={() => setAssistId(pIdStr)}
                    className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left transition-all cursor-pointer ${
                      isSelected
                        ? 'border-[#FC480D] bg-orange-50/50 shadow-xs'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon
                        icon="ph:arrow-elbow-down-right-bold"
                        className={`w-5 h-5 ${isSelected ? 'text-[#FC480D]' : 'text-gray-400'}`}
                      />
                      <span
                        className={`text-sm font-semibold ${
                          isSelected ? 'text-[#0E1726]' : 'text-gray-700'
                        }`}
                      >
                        {p.name}
                      </span>
                    </div>
                    <div
                      className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                        isSelected
                          ? 'border-[#FC480D] bg-[#FC480D] text-white'
                          : 'border-gray-300 bg-white'
                      }`}
                    >
                      {isSelected && <Icon icon="ph:check-bold" className="w-3 h-3" />}
                    </div>
                  </button>
                );
              })}

              {/* Tanpa Assist Option */}
              <button
                type="button"
                onClick={() => setAssistId(ASSIST_NONE_VALUE)}
                className={`w-full p-3 rounded-2xl border flex items-center justify-between text-left transition-all cursor-pointer ${
                  assistId === ASSIST_NONE_VALUE
                    ? 'border-[#FC480D] bg-orange-50/50 shadow-xs'
                    : 'border-gray-200 hover:border-gray-300 bg-white'
                }`}
              >
                <div className="flex items-center gap-3">
                  <Icon
                    icon="ph:arrow-elbow-down-right-bold"
                    className={`w-5 h-5 ${
                      assistId === ASSIST_NONE_VALUE ? 'text-[#FC480D]' : 'text-gray-400'
                    }`}
                  />
                  <span
                    className={`text-sm font-semibold ${
                      assistId === ASSIST_NONE_VALUE ? 'text-[#0E1726]' : 'text-gray-700'
                    }`}
                  >
                    Tanpa assist
                  </span>
                </div>
                <div
                  className={`w-5 h-5 rounded-full border flex items-center justify-center ${
                    assistId === ASSIST_NONE_VALUE
                      ? 'border-[#FC480D] bg-[#FC480D] text-white'
                      : 'border-gray-300 bg-white'
                  }`}
                >
                  {assistId === ASSIST_NONE_VALUE && (
                    <Icon icon="ph:check-bold" className="w-3 h-3" />
                  )}
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* Footer Buttons */}
        <div className="grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-11 px-4 rounded-full border border-gray-200 text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-all cursor-pointer"
          >
            Batal
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave || saving}
            className={`h-11 px-4 rounded-full bg-[#FC480D] hover:bg-[#e03d07] text-white text-sm font-bold shadow-md transition-all cursor-pointer flex items-center justify-center gap-2 ${
              !canSave || saving ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
            }`}
          >
            {saving ? (
              <>
                <Icon icon="ph:spinner-bold" className="w-4 h-4 animate-spin" />
                <span>Menyimpan...</span>
              </>
            ) : (
              <span>Simpan Gol</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CatatGolModal;
